import type {
  CtaLayout,
  HeaderVariant,
  PartialConfigurableText,
} from './types';

// A variant is pure config of the one base modal: text overrides + the two
// structural sockets. No forks/chunks.
export type ModalVariantEntry = {
  configuredText?: PartialConfigurableText;
  headerVariant?: HeaderVariant;
  ctaLayout?: CtaLayout;
};

const SPEED_SUBHEADER = '5 seconds with Google — really';

// v5 2^3 factorial: card=headerVariant, gexp=ctaLayout, speed=subheader.
// Card cells set subheader:'' (no line unless speed is on); labels stay
// uniform so gexp is purely the expander.
export const MODAL_VARIANT_REGISTRY: Record<string, ModalVariantEntry> = {
  control: {},

  speed_only: {
    configuredText: { buttonsPage: { subheader: SPEED_SUBHEADER } },
  },
  gexp_only: {
    ctaLayout: 'googleOnlyExpander',
  },
  gexp_speed: {
    ctaLayout: 'googleOnlyExpander',
    configuredText: { buttonsPage: { subheader: SPEED_SUBHEADER } },
  },
  card_only: {
    headerVariant: 'yourPaperCard',
    configuredText: { buttonsPage: { subheader: '' } },
  },
  card_speed: {
    headerVariant: 'yourPaperCard',
    configuredText: { buttonsPage: { subheader: SPEED_SUBHEADER } },
  },
  card_gexp: {
    headerVariant: 'yourPaperCard',
    ctaLayout: 'googleOnlyExpander',
    configuredText: { buttonsPage: { subheader: '' } },
  },
  card_gexp_speed: {
    headerVariant: 'yourPaperCard',
    ctaLayout: 'googleOnlyExpander',
    configuredText: { buttonsPage: { subheader: SPEED_SUBHEADER } },
  },
};

export function lookupModalVariant(bucket: string): ModalVariantEntry {
  return MODAL_VARIANT_REGISTRY[bucket] ?? {};
}

// Deep-merges variant text over caller text, per page; variant keys win.
export function mergeConfiguredText(
  caller: PartialConfigurableText | undefined,
  variant: PartialConfigurableText | undefined,
): PartialConfigurableText | undefined {
  if (!variant) return caller;
  if (!caller) return variant;
  return {
    buttonsPage: { ...caller.buttonsPage, ...variant.buttonsPage },
    emailEntryPage: { ...caller.emailEntryPage, ...variant.emailEntryPage },
    emailRegistrationPage: {
      ...caller.emailRegistrationPage,
      ...variant.emailRegistrationPage,
    },
    emailLoginPage: { ...caller.emailLoginPage, ...variant.emailLoginPage },
  };
}

// Replaces `{paperTitle}` / `{authorName}` tokens in variant copy with the
// caller's work context, falling back to a generic phrase when absent.
export const PAPER_TITLE_FALLBACK = 'this paper';
export const AUTHOR_NAME_FALLBACK = 'the authors';

export type WorkContext = {
  workTitle?: string;
  authorName?: string;
};

export function substituteWorkContext(
  text: PartialConfigurableText | undefined,
  ctx: WorkContext,
): PartialConfigurableText | undefined {
  if (!text) return text;
  const title = ctx.workTitle?.trim() || PAPER_TITLE_FALLBACK;
  const author = ctx.authorName?.trim() || AUTHOR_NAME_FALLBACK;
  // Function replacer so `$&`/`$1` in titles aren't treated as replace tokens.
  const sub = (s: string): string =>
    s
      .replace(/\{paperTitle\}/g, () => title)
      .replace(/\{authorName\}/g, () => author);

  // Preserve the input's key set (absent stays absent) so it doesn't clobber
  // defaults in LoginModal's downstream spread merge.
  const subFields = <T extends Record<string, string | undefined>>(
    obj: T,
  ): T => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = sub(v);
    }
    return out as T;
  };

  return {
    buttonsPage: text.buttonsPage && subFields(text.buttonsPage),
    emailEntryPage: text.emailEntryPage && subFields(text.emailEntryPage),
    emailRegistrationPage:
      text.emailRegistrationPage && subFields(text.emailRegistrationPage),
    emailLoginPage: text.emailLoginPage && subFields(text.emailLoginPage),
  };
}
